import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  Check,
  ListFilter,
  MessageSquare,
  Pencil,
  Plus,
  Search,
  Trash2,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import type { BoardActivityItem, BoardPersonMini } from '@hygge/shared';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useBoardActivity } from '@/hooks/use-board-overview';
import { activityVerb, activityVerbShort, timeAgo } from '../../lib/activity-format';
import {
  ACTIVITY_CATEGORY_LABEL,
  activityCategory,
  clockTime,
  filterActivity,
  groupActivity,
  type ActivityCategory,
  type ActivitySession,
} from '../../lib/activity-group';

interface BoardActivityTabProps {
  boardId: string;
}

function personName(
  person: BoardPersonMini | null,
  format: ReturnType<typeof useEmployeeNameFormatter>,
): string {
  if (!person) return 'Someone';
  return format({ firstName: person.firstName, lastName: person.lastName });
}

function personInitials(person: BoardPersonMini | null): string {
  if (!person) return '?';
  return `${person.firstName[0] ?? ''}${person.lastName[0] ?? ''}`.toUpperCase();
}

/**
 * A single board-activity entry — a project update card or a one-line task
 * event. Exported so the Overview sidebar's Activity card reuses it verbatim.
 */
export function ActivityItemRow({
  item,
  onOpenTask,
}: {
  item: BoardActivityItem;
  onOpenTask: (taskKey: string) => void;
}) {
  const format = useEmployeeNameFormatter();

  if (item.kind === 'update') {
    return (
      <li className="flex gap-2.5">
        <Avatar className="mt-0.5 h-6 w-6 shrink-0">
          {item.author?.avatarUrl && <AvatarImage src={item.author.avatarUrl} />}
          <AvatarFallback className="bg-hygge/10 text-[9px] font-medium text-hygge">
            {personInitials(item.author)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-xs leading-5">
            <span className="font-medium text-foreground">{personName(item.author, format)}</span>
            <Badge variant="secondary" className="px-1.5 py-0 text-[10px] font-medium">
              Update
            </Badge>
            <span className="text-[11px] text-muted-foreground">{timeAgo(item.createdAt)}</span>
          </p>
          <div className="mt-1.5 whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-2.5 text-xs leading-relaxed text-foreground/90">
            {item.body}
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="flex gap-2.5">
      <Avatar className="mt-0.5 h-6 w-6 shrink-0">
        {item.actor?.avatarUrl && <AvatarImage src={item.actor.avatarUrl} />}
        <AvatarFallback className="bg-muted text-[9px] font-medium text-muted-foreground">
          {personInitials(item.actor)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="text-xs leading-5 text-foreground/90">
          <span className="font-medium text-foreground">{personName(item.actor, format)}</span>{' '}
          {activityVerb(item)}{' '}
          <button
            type="button"
            onClick={() => onOpenTask(item.taskKey)}
            className="cursor-pointer font-mono text-[11px] text-muted-foreground hover:text-foreground hover:underline"
            title={item.taskTitle}
          >
            {item.taskKey}
          </button>
          <span className="ml-1.5 text-[11px] text-muted-foreground">{timeAgo(item.createdAt)}</span>
        </p>
      </div>
    </li>
  );
}

/** Icon + tone per event category, so a feed can be scanned by shape and colour
 *  instead of read line by line. */
const CATEGORY_ICON: Record<ActivityCategory, { Icon: typeof Plus; tone: string }> = {
  created: { Icon: Plus, tone: 'text-emerald-600 dark:text-emerald-400' },
  status: { Icon: ArrowRight, tone: 'text-hygge' },
  assignee: { Icon: UserRound, tone: 'text-violet-600 dark:text-violet-400' },
  content: { Icon: Pencil, tone: 'text-muted-foreground' },
  update: { Icon: MessageSquare, tone: 'text-hygge' },
  other: { Icon: Pencil, tone: 'text-muted-foreground' },
};

/** One event inside a session: type icon, verb, and the task's real title (the
 *  key alone doesn't tell you which task you're looking at). */
function SessionEvent({
  item,
  onOpenTask,
}: {
  item: Extract<BoardActivityItem, { kind: 'task' }>;
  onOpenTask: (taskKey: string) => void;
}) {
  const { Icon, tone } = item.type === 'deleted'
    ? { Icon: Trash2, tone: 'text-destructive' }
    : CATEGORY_ICON[activityCategory(item)];

  return (
    <li className="flex items-start gap-2">
      <Icon className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', tone)} aria-hidden="true" />
      {/* Task first, change second — the task is what you scan for, and it keeps
          every verb reading naturally ("Slack migration — changed status from…"). */}
      <p className="min-w-0 text-xs leading-5 text-muted-foreground">
        <button
          type="button"
          onClick={() => onOpenTask(item.taskKey)}
          className="cursor-pointer font-medium text-foreground hover:underline"
        >
          {item.taskTitle}
        </button>{' '}
        <span className="font-mono text-[11px] text-muted-foreground/80">{item.taskKey}</span>
        {' — '}
        {activityVerbShort(item)}
      </p>
    </li>
  );
}

/** A run of events by one person: the avatar and name are drawn once for the whole
 *  run instead of once per event. */
function SessionBlock({
  session,
  onOpenTask,
}: {
  session: ActivitySession;
  onOpenTask: (taskKey: string) => void;
}) {
  const format = useEmployeeNameFormatter();
  const isUpdate = session.kind === 'update';
  const first = session.items[0];

  return (
    <li className="flex gap-3">
      <Avatar className="mt-0.5 h-7 w-7 shrink-0">
        {session.actor?.avatarUrl && <AvatarImage src={session.actor.avatarUrl} />}
        <AvatarFallback
          className={cn(
            'text-[9px] font-medium',
            isUpdate ? 'bg-hygge/10 text-hygge' : 'bg-muted text-muted-foreground',
          )}
        >
          {personInitials(session.actor)}
        </AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-x-1.5 text-xs leading-5">
          <span className="font-medium text-foreground">{personName(session.actor, format)}</span>
          {isUpdate && (
            <Badge variant="secondary" className="px-1.5 py-0 text-[10px] font-medium">
              Update
            </Badge>
          )}
          <span
            className="text-[11px] text-muted-foreground"
            title={new Date(session.at).toLocaleString()}
          >
            {clockTime(session.at)}
          </span>
        </p>

        {isUpdate && first.kind === 'update' ? (
          <div className="mt-1.5 whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-2.5 text-xs leading-relaxed text-foreground/90">
            {first.body}
          </div>
        ) : (
          <ul className="mt-1 space-y-1">
            {session.items.map((item) =>
              item.kind === 'task' ? (
                <SessionEvent key={item.id} item={item} onOpenTask={onOpenTask} />
              ) : null,
            )}
          </ul>
        )}
      </div>
    </li>
  );
}

/** Popover with a checkbox-style list — used for both the people and type filters. */
function FilterPopover<T extends string>({
  label,
  icon,
  options,
  selected,
  onToggle,
  onClear,
}: {
  label: string;
  icon: React.ReactNode;
  options: { value: T; label: string }[];
  selected: T[];
  onToggle: (value: T) => void;
  onClear: () => void;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
          {icon}
          {selected.length === 0 ? label : `${label} · ${selected.length}`}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        {options.length === 0 ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">Nothing to filter by yet.</p>
        ) : (
          <>
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onToggle(opt.value)}
                className="flex w-full cursor-pointer items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-muted"
              >
                <span className="truncate">{opt.label}</span>
                {selected.includes(opt.value) && <Check className="h-3.5 w-3.5 shrink-0 text-hygge" />}
              </button>
            ))}
            {selected.length > 0 && (
              <button
                type="button"
                onClick={onClear}
                className="mt-1 w-full cursor-pointer border-t border-border px-2 pt-2 pb-1 text-left text-xs text-muted-foreground hover:text-foreground"
              >
                Clear
              </button>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Scroll container for the tab.
 *
 * The tab is mounted inside a `flex-1 overflow-hidden` pane, so without its own
 * scroller a long feed is simply clipped — the page itself cannot scroll and
 * everything below the fold is unreachable. The scroller is full-width and the
 * reading column is centred inside it, so the scrollbar sits at the edge of the
 * pane rather than halfway across it (same shape as the Overview tab).
 */
function ActivityShell({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="h-full overflow-y-auto">
      <div className={cn('max-w-4xl px-6 py-6', className)}>{children}</div>
    </div>
  );
}

export function BoardActivityTab({ boardId }: BoardActivityTabProps) {
  const navigate = useNavigate();
  const { data: items, isLoading } = useBoardActivity(boardId);
  const format = useEmployeeNameFormatter();

  const [search, setSearch] = useState('');
  const [actorIds, setActorIds] = useState<string[]>([]);
  const [categories, setCategories] = useState<ActivityCategory[]>([]);

  const openTask = (taskKey: string) =>
    navigate({ to: '/boards', search: (prev: Record<string, unknown>) => ({ ...prev, task: taskKey }) });

  // Filter options come from the loaded feed — offering people or event types that
  // produce zero rows would be a dead end.
  const people = useMemo(() => {
    const seen = new Map<string, BoardPersonMini>();
    for (const item of items ?? []) {
      const person = item.kind === 'update' ? item.author : item.actor;
      if (person && !seen.has(person.id)) seen.set(person.id, person);
    }
    return [...seen.values()];
  }, [items]);

  const presentCategories = useMemo(() => {
    const seen = new Set<ActivityCategory>();
    for (const item of items ?? []) seen.add(activityCategory(item));
    return [...seen];
  }, [items]);

  const filtered = useMemo(
    () => filterActivity(items ?? [], { search, actorIds, categories }),
    [items, search, actorIds, categories],
  );
  const days = useMemo(() => groupActivity(filtered, new Date()), [filtered]);

  const filtersActive = search.trim() !== '' || actorIds.length > 0 || categories.length > 0;
  const clearAll = () => {
    setSearch('');
    setActorIds([]);
    setCategories([]);
  };

  const toggle = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  if (isLoading) {
    return <ActivityShell className="text-sm text-muted-foreground">Loading activity…</ActivityShell>;
  }

  if (!items || items.length === 0) {
    return (
      <ActivityShell className="py-16">
        <p className="text-sm font-medium text-foreground">No activity yet</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Board updates and task changes will show up here as the team works.
        </p>
      </ActivityShell>
    );
  }

  return (
    <ActivityShell>
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search activity"
            className="h-8 pl-8 text-xs"
          />
        </div>

        <FilterPopover
          label="People"
          icon={<Users className="h-3.5 w-3.5" />}
          options={people.map((p) => ({ value: p.id, label: personName(p, format) }))}
          selected={actorIds}
          onToggle={(id) => setActorIds((prev) => toggle(prev, id))}
          onClear={() => setActorIds([])}
        />

        <FilterPopover
          label="Type"
          icon={<ListFilter className="h-3.5 w-3.5" />}
          options={presentCategories.map((c) => ({ value: c, label: ACTIVITY_CATEGORY_LABEL[c] }))}
          selected={categories}
          onToggle={(c) => setCategories((prev) => toggle(prev, c))}
          onClear={() => setCategories([])}
        />

        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={clearAll} className="h-8 gap-1 text-xs">
            <X className="h-3.5 w-3.5" />
            Clear
          </Button>
        )}
      </div>

      {days.length === 0 ? (
        <p className="py-10 text-sm text-muted-foreground">No activity matches these filters.</p>
      ) : (
        <div className="space-y-7">
          {days.map((day) => (
            <section key={day.key}>
              <div className="mb-3 flex items-center gap-3">
                <h3 className="text-xs font-medium text-muted-foreground">{day.label}</h3>
                <span className="h-px flex-1 bg-border" />
              </div>
              <ul className="space-y-5">
                {day.sessions.map((session) => (
                  <SessionBlock key={session.key} session={session} onOpenTask={openTask} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </ActivityShell>
  );
}
